import React, { useEffect } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, 
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell
} from 'recharts';
import {
  Briefcase, Network, TrendingUp, CircleDollarSign,
  Target, X, Users, Award, MapPin, GraduationCap
} from 'lucide-react';

// Complete color palette definitions
const CHART_COLORS = {
  // Skills palette - Blues
  skills: {
    colors: [
      '#1E40AF', // Highest - darkest blue
      '#2563EB',
      '#3B82F6',
      '#60A5FA',
      '#93C5FD',
      '#BFDBFE', // Lowest - lightest blue
    ],
    background: '#F8FAFF',
    title: 'Skills Distribution'
  },
  // Companies palette - Purples
  companies: {
    colors: [
      '#6D28D9', // Primary purple
      '#7C3AED',
      '#8B5CF6',
      '#A78BFA',
      '#C4B5FD',
      '#DDD6FE',
    ],
    background: '#F9F6FF',
    title: 'Company Distribution'
  },
  // Locations palette - Greens
  locations: {
    colors: [
      '#065F46', // Darkest green
      '#047857',
      '#059669',
      '#10B981',
      '#34D399',
      '#6EE7B7',
    ],
    background: '#ECFDF5',
    title: 'Geographical Distribution'
  },
  // Departments palette - Oranges
  departments: {
    colors: [
      '#92400E', // Darkest orange
      '#B45309',
      '#D97706',
      '#F59E0B',
      '#FBBF24',
      '#FCD34D',
    ],
    background: '#FFFBEB',
    title: 'Department Distribution'
  }
};

const CustomTooltip = ({ payload, label, active }) => {
  if (!active || !payload) return null;

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
      <p className="font-medium text-gray-900">{label}</p>
      {payload.map((item, index) => (
        <p key={index} className="text-sm text-gray-600">
          Count: {item.value}
        </p>
      ))}
    </div>
  );
};

const processAnalytics = (alumni) => {
  if (!alumni?.length) return {
    skillsData: [],
    companiesData: [],
    locationData: [],
    departmentData: [],
    stats: {
      total: 0,
      employed: 0,
      employmentRate: "0"
    }
  };

  // Process skills
  const skillsCount = {};
  alumni.forEach(alumnus => {
    alumnus.professionalInfo?.skills?.forEach(skill => {
      skillsCount[skill] = (skillsCount[skill] || 0) + 1;
    });
  });

  // Process companies
  const companiesCount = {};
  alumni.forEach(alumnus => {
    const currentCompany = alumnus.professionalInfo?.experiences?.find(exp => exp.isCurrent)?.company;
    if (currentCompany) {
      companiesCount[currentCompany] = (companiesCount[currentCompany] || 0) + 1;
    }
  });

  // Process locations
  const locationCount = {};
  alumni.forEach(alumnus => {
    const city = alumnus.personalInfo?.currentLocation?.address?.split(',')[0];
    if (city) {
      locationCount[city] = (locationCount[city] || 0) + 1;
    }
  });

  // Process departments
  const departmentCount = {};
  alumni.forEach(alumnus => {
    const dept = alumnus.academicInfo?.department;
    if (dept) {
      departmentCount[dept] = (departmentCount[dept] || 0) + 1;
    }
  });

  // Calculate employment stats
  const total = alumni.length;
  const employed = alumni.filter(a => a.professionalInfo?.currentlyEmployed).length;

  return {
    skillsData: Object.entries(skillsCount)
      .map(([skill, count]) => ({ skill, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10),
    
    companiesData: Object.entries(companiesCount)
      .map(([company, count]) => ({ company, count }))
      .sort((a, b) => b.count - a.count),
    
    locationData: Object.entries(locationCount)
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count),
    
    departmentData: Object.entries(departmentCount)
      .map(([department, count]) => ({ department, count }))
      .sort((a, b) => b.count - a.count),
    
    stats: {
      total,
      employed,
      employmentRate: ((employed / total) * 100).toFixed(1)
    }
  };
};

const MetricCard = ({ title, value, icon: Icon, trend, description, colorScheme }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <h3 className="text-3xl font-bold mt-2 mb-1">{value}</h3>
        {description && <p className="text-sm text-gray-500">{description}</p>}
        {trend && (
          <div className="flex items-center mt-2">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">{trend}% growth</span>
          </div>
        )}
      </div>
      <div className={`p-3 rounded-lg ${colorScheme}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
);

const ChartContainer = ({ title, children, colorScheme, icon: Icon }) => (
  <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 ${colorScheme?.background}`}>
    <div className="flex items-center space-x-2 mb-6">
      <Icon className="w-5 h-5 text-gray-600" />
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="h-80">
      {children}
    </div>
  </div>
);

const SkillsChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} layout="vertical">
      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
      <XAxis type="number" />
      <YAxis dataKey="skill" type="category" width={120} tick={{ fill: '#374151', fontSize: 12 }} />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="count">
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.skills.colors[Math.min(index, CHART_COLORS.skills.colors.length - 1)]}
          />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const CompaniesChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart>
      <Pie
        data={data}
        dataKey="count"
        nameKey="company"
        cx="50%"
        cy="50%"
        innerRadius={60}
        outerRadius={80}
        paddingAngle={5}
      >
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.companies.colors[Math.min(index, CHART_COLORS.companies.colors.length - 1)]}
          />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
      <Legend verticalAlign="middle" align="right" layout="vertical" />
    </PieChart>
  </ResponsiveContainer>
);

const LocationsChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
      <XAxis dataKey="city" />
      <YAxis />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="count">
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.locations.colors[Math.min(index, CHART_COLORS.locations.colors.length - 1)]}
          />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const DepartmentsChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart>
      <Pie
        data={data}
        dataKey="count"
        nameKey="department"
        cx="50%"
        cy="50%"
        innerRadius={60}
        outerRadius={80}
        paddingAngle={5}
      >
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.departments.colors[Math.min(index, CHART_COLORS.departments.colors.length - 1)]}
          />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
      <Legend verticalAlign="middle" align="right" layout="vertical" />
    </PieChart>
  </ResponsiveContainer>
);

const AnalyticsModal = ({ isOpen, onClose, alumni }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !alumni) return null;

  const { skillsData, companiesData, locationData, departmentData, stats } = processAnalytics(alumni);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Network Insights</h2>
                <p className="text-sm text-gray-500">Comprehensive analytics of our alumni community</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="p-6 max-h-[calc(90vh-5rem)] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Total Alumni"
                value={stats.total}
                icon={Users}
                colorScheme="bg-blue-500"
                description="Active community members"
              />
              <MetricCard
                title="Employment Rate"
                value={`${stats.employmentRate}%`}
                icon={Briefcase}
                colorScheme="bg-green-500"
                trend="12"
              />
              <MetricCard
                title="Companies"
                value={companiesData.length}
                icon={Target}
                colorScheme="bg-purple-500"
                description="Diverse industries"
              />
              <MetricCard
                title="Global Presence"
                value={locationData.length}
                icon={MapPin}
                colorScheme="bg-orange-500"
                description="Cities worldwide"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartContainer 
                title="Top Skills & Expertise" 
                colorScheme={CHART_COLORS.skills}
                icon={Award}
              >
                <SkillsChart data={skillsData} />
              </ChartContainer>

              <ChartContainer 
                title="Company Distribution" 
                colorScheme={CHART_COLORS.companies}
                icon={Briefcase}
              >
                <CompaniesChart data={companiesData} />
              </ChartContainer>

              <ChartContainer 
                title="Geographical Distribution" 
                colorScheme={CHART_COLORS.locations}
                icon={MapPin}
              >
                <LocationsChart data={locationData} />
              </ChartContainer>

              <ChartContainer 
                title="Academic Department Distribution" 
                colorScheme={CHART_COLORS.departments}
                icon={GraduationCap}
              >
                <DepartmentsChart data={departmentData} />
              </ChartContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal;